export type CacheOnlyFailureReport = {
  nodeId: string;
  nodeName: string;
  stepIndex: number;
  nodeHash?: string;
  cacheId?: string;
  failureClass: string;
  cacheOutcome: string;
  cacheOutcomeReason?: string;
  failureCategory?: string;
  failedStepOrder?: number;
  error?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOptionalString(record: Record<string, unknown>, key: string): boolean {
  return record[key] === undefined || typeof record[key] === 'string';
}

export function isCacheOnlyFailureReport(value: unknown): value is CacheOnlyFailureReport {
  if (!isRecord(value)) return false;

  return (
    typeof value.nodeId === 'string' &&
    typeof value.nodeName === 'string' &&
    typeof value.stepIndex === 'number' &&
    typeof value.failureClass === 'string' &&
    typeof value.cacheOutcome === 'string' &&
    hasOptionalString(value, 'nodeHash') &&
    hasOptionalString(value, 'cacheId') &&
    hasOptionalString(value, 'cacheOutcomeReason') &&
    hasOptionalString(value, 'failureCategory') &&
    (value.failedStepOrder === undefined || typeof value.failedStepOrder === 'number') &&
    hasOptionalString(value, 'error')
  );
}

export function getCacheOnlyFailure(
  metadataJson: Record<string, unknown> | null | undefined
): CacheOnlyFailureReport | null {
  const report = metadataJson?.cacheOnlyFailure;
  return isCacheOnlyFailureReport(report) ? report : null;
}

export function formatCacheOnlyFailure(report: CacheOnlyFailureReport): string[] {
  const lines = [
    'Cache-only failure:',
    `  Node: ${report.nodeName}`,
    `  Failure class: ${report.failureClass}`,
    `  Cache outcome: ${report.cacheOutcome}`,
  ];

  if (report.cacheOutcomeReason) {
    lines.push(`  Reason: ${report.cacheOutcomeReason}`);
  }
  if (report.failedStepOrder !== undefined) {
    lines.push(`  Failed step order: ${report.failedStepOrder}`);
  }
  if (report.error) {
    lines.push(`  Error: ${report.error}`);
  }

  return lines;
}
